// Dataset demo locale (corrisponde a seed.sql). Usato come fallback quando
// l'API non è raggiungibile o l'utente non è ancora autenticato, così la UI
// è ispezionabile in sviluppo. In produzione i dati arrivano da /api.
import type { AlertItem, AlertsResponse, DocumentDTO, MemberDTO, WarrantyDTO, WarrantyListResponse } from '@shared/types';
import { daysLeft, progress, warrantyStatus } from '@shared/warranty';

// Membri demo (§6.15): titolare + due familiari.
const M_SELF = 'm_self';
const M_ANNA = 'm_anna';
const M_LUCA = 'm_luca';

export const DEMO_USER: import('@shared/types').MeDTO = {
  id: 'u_demo',
  display_name: 'Mario Rossi',
  email: 'mario.rossi@example.it',
  email_verified: true,
  phone: null,
  avatar_url: null,
  security: { pin_set: false, biometric_enabled: false, biometric_registered: false, auto_lock_seconds: 60 },
};

export const DEMO_DOCUMENTS: DocumentDTO[] = [
  {
    id: 'd_cie_mario', type: 'cie', title: "Carta d'identità", holder: 'Mario Rossi',
    number: 'CA75920AB', issued_by: 'Verona', issued_at: '2019-07-14', expires_at: '2029-07-14',
    fields: { data_nascita: '1988-07-14', luogo_nascita: 'Verona', cittadinanza: 'Italiana', comune_rilascio: 'Verona' },
    has_front: true, has_back: true, has_pdf: true, thumb_url: null, owner_member_id: M_SELF, created_at: '', updated_at: '',
  },
  {
    id: 'd_pat_mario', type: 'patente', title: 'Patente di guida', holder: 'Mario Rossi',
    number: 'VR5839201X', issued_by: 'MIT-UCO', issued_at: '2015-03-01', expires_at: '2031-03-01',
    fields: { categorie: 'B', punti: 30 }, has_front: true, has_back: false, has_pdf: false,
    thumb_url: null, owner_member_id: M_SELF, created_at: '', updated_at: '',
  },
  {
    id: 'd_pass_mario', type: 'passaporto', title: 'Passaporto', holder: 'Mario Rossi',
    number: 'YA1234567', issued_by: 'Questura di Verona', issued_at: '2018-05-20', expires_at: '2028-05-20',
    fields: { autorita: 'Questura di Verona' }, has_front: true, has_back: false, has_pdf: false,
    thumb_url: null, owner_member_id: M_SELF, created_at: '', updated_at: '',
  },
  {
    id: 'd_ts_mario', type: 'tessera_sanitaria', title: 'Tessera sanitaria', holder: 'Mario Rossi',
    number: 'RSSMRA88L14L781K', issued_by: 'MEF', issued_at: '2020-01-01', expires_at: '2026-12-31',
    fields: { codice_fiscale: 'RSSMRA88L14L781K' }, has_front: true, has_back: true, has_pdf: false,
    thumb_url: null, owner_member_id: M_SELF, created_at: '', updated_at: '',
  },
  {
    id: 'd_cie_anna', type: 'cie', title: "Carta d'identità", holder: 'Anna Rossi',
    number: 'CA90211CD', issued_by: 'Verona', issued_at: '2021-09-10', expires_at: '2031-09-10',
    fields: { data_nascita: '1990-02-03', relazione: 'familiare' }, has_front: true, has_back: true, has_pdf: false,
    thumb_url: null, owner_member_id: M_ANNA, created_at: '', updated_at: '',
  },
];

function warranty(
  id: string, product: string, category: string, seller: string, amountCents: number,
  purchasedAt: string, months: number, attachments: WarrantyDTO['attachments'] = [],
): WarrantyDTO {
  const expires = addMonthsLocal(purchasedAt, months);
  const days = daysLeft(expires);
  return {
    id, product, category, seller, amount_cents: amountCents, currency: 'EUR',
    purchased_at: purchasedAt, warranty_months: months, expires_at: expires, notes: null,
    days_left: days, progress: progress(purchasedAt, expires), status: warrantyStatus(days), attachments,
    owner_member_id: M_SELF,
  };
}
function addMonthsLocal(iso: string, m: number): string {
  const d = new Date(iso + 'T00:00:00Z');
  d.setUTCMonth(d.getUTCMonth() + m);
  return d.toISOString().slice(0, 10);
}

export const DEMO_WARRANTIES: WarrantyDTO[] = [
  warranty('w_iphone', 'iPhone 15', 'Elettronica', 'MediaWorld', 97900, '2024-06-12', 24, [
    { id: 'a_scontrino', filename: 'scontrino.pdf', mime: 'application/pdf', size_bytes: 245760 },
    { id: 'a_fattura', filename: 'fattura.pdf', mime: 'application/pdf', size_bytes: 184320 },
  ]),
  warranty('w_bosch', 'Lavatrice Bosch', 'Elettrodomestici', 'Euronics', 49900, '2023-09-01', 24),
  warranty('w_macbook', 'MacBook Air', 'Informatica', 'Apple', 134900, '2025-02-15', 24),
];

/** Membri demo (§6.15) con conteggio documenti+garanzie per profilo. */
export function demoMembers(): { items: MemberDTO[] } {
  const count = (mid: string) =>
    DEMO_DOCUMENTS.filter((d) => d.owner_member_id === mid).length +
    DEMO_WARRANTIES.filter((w) => w.owner_member_id === mid).length;
  return {
    items: [
      { id: M_SELF, display_name: 'Mario Rossi', relation: 'self', is_self: true, doc_count: count(M_SELF) },
      { id: M_ANNA, display_name: 'Anna Rossi', relation: 'spouse', is_self: false, doc_count: count(M_ANNA) },
      { id: M_LUCA, display_name: 'Luca Rossi', relation: 'child', is_self: false, doc_count: count(M_LUCA) },
    ],
  };
}

/** Avvisi di scadenza demo (§6.12): documenti + garanzie nei prossimi 60 giorni. */
export function demoAlerts(): AlertsResponse {
  const items: AlertItem[] = [];
  for (const d of DEMO_DOCUMENTS) {
    if (!d.expires_at) continue;
    const left = daysLeft(d.expires_at);
    if (left < 0 || left > 60) continue;
    items.push({ kind: 'document', id: d.id, title: d.title, expires_at: d.expires_at, days_left: left, severity: left <= 30 ? 'critical' : 'upcoming' });
  }
  for (const w of DEMO_WARRANTIES) {
    if (!w.expires_at) continue;
    const left = daysLeft(w.expires_at);
    if (left < 0 || left > 60) continue;
    items.push({ kind: 'warranty', id: w.id, title: `Garanzia ${w.product}`, expires_at: w.expires_at, days_left: left, severity: left <= 30 ? 'critical' : 'upcoming' });
  }
  items.sort((a, b) => a.days_left - b.days_left);
  return { summary: { within_60: items.length }, items };
}

export function demoWarrantyResponse(): WarrantyListResponse {
  return {
    summary: {
      active: DEMO_WARRANTIES.filter((w) => w.status === 'active').length,
      expiring: DEMO_WARRANTIES.filter((w) => w.status === 'expiring').length,
    },
    items: DEMO_WARRANTIES,
  };
}
